import { supabase } from '../../utils/supabase';
import { DojosRow, SeasonsRow } from '../../types/database';
import { statsCache } from '../../utils/statsCache';

export class DojoSeasonService {
  async loadDojos(): Promise<DojosRow[]> {
    try {
      const { data, error } = await supabase
        .from('dojos')
        .select('*')
        .eq('is_deleted', 'n')
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('[V100] 加载道场失败:', err);
      return [];
    }
  }

  async loadSeasons(): Promise<SeasonsRow[]> {
    try {
      const { data, error } = await supabase
        .from('seasons')
        .select('*')
        .eq('is_deleted', 'n')
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('[V100] 加载赛季失败:', err);
      return [];
    }
  }

  async addDojo(name: string, existingDojos: DojosRow[]): Promise<DojosRow | null> {
    const trimmedName = name.trim();
    if (!trimmedName) return null;

    const isDuplicate = existingDojos.some(d => d.name === trimmedName);
    if (isDuplicate) {
      throw new Error('该道场名称已存在');
    }

    try {
      const { data, error } = await supabase
        .from('dojos')
        .insert({ name: trimmedName })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error('该道场名称已存在');
        }
        throw error;
      }

      return data;
    } catch (err) {
      console.error('[V100] 添加道场失败:', err);
      throw err;
    }
  }

  async updateDojo(dojoId: number, name: string): Promise<void> {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    try {
      const { error } = await supabase
        .from('dojos')
        .update({ name: trimmedName })
        .eq('id', dojoId);

      if (error) throw error;
    } catch (err) {
      console.error('[V100] 更新道场失败:', err);
      throw err;
    }
  }

  async deleteDojo(dojoId: number): Promise<void> {
    try {
      await supabase.from('seasons').delete().eq('dojo_id', dojoId);
      const { error } = await supabase.from('dojos').delete().eq('id', dojoId);
      if (error) throw error;
      
      // V620: 清除该道场相关的所有缓存
      for (let seasonId = 1; seasonId <= 10; seasonId++) {
        statsCache.remove(`player_stats:${dojoId}:${seasonId}`);
        statsCache.remove(`penalty_records:${dojoId}:${seasonId}`);
      }
      statsCache.remove('player_stats:all:all');
      statsCache.remove('penalty_records:all:all');
      console.log(`[V620] 已清除道场 ${dojoId} 相关缓存`);
    } catch (err) {
      console.error('[V100] 删除道场失败:', err);
      throw err;
    }
  }

  async addSeason(name: string, dojoId: string, existingSeasons: SeasonsRow[]): Promise<SeasonsRow | null> {
    const trimmedName = name.trim();
    if (!trimmedName || !dojoId) return null;

    const isDuplicate = existingSeasons.some(
      s => s.name === trimmedName && s.dojo_id === Number(dojoId)
    );
    if (isDuplicate) {
      throw new Error('该道场下已存在同名赛季');
    }

    try {
      const { data, error } = await supabase
        .from('seasons')
        .insert({
          name: trimmedName,
          dojo_id: Number(dojoId),
        })
        .select()
        .single();

      if (error) {
        if (error.message?.includes('unique_season_name_per_dojo')) {
          throw new Error('该道场下已存在同名赛季');
        }
        throw error;
      }

      return data;
    } catch (err) {
      console.error('[V100] 添加赛季失败:', err);
      throw err;
    }
  }

  async updateSeason(seasonId: number, name: string, dojoId: string): Promise<void> {
    const trimmedName = name.trim();
    if (!trimmedName || !dojoId) return;

    try {
      const { error } = await supabase
        .from('seasons')
        .update({
          name: trimmedName,
          dojo_id: Number(dojoId),
        })
        .eq('id', seasonId);

      if (error) {
        if (error.message?.includes('unique_season_name_per_dojo')) {
          throw new Error('该道场下已存在同名赛季');
        }
        throw error;
      }
    } catch (err) {
      console.error('[V100] 更新赛季失败:', err);
      throw err;
    }
  }

  async deleteSeason(seasonId: number): Promise<void> {
    try {
      const { error } = await supabase
        .from('seasons')
        .update({ is_active: false, is_deleted: 'y' })
        .eq('id', seasonId);

      if (error) throw error;
      
      // V620: 清除该赛季相关的所有缓存
      for (let dojoId = 1; dojoId <= 10; dojoId++) {
        statsCache.remove(`player_stats:${dojoId}:${seasonId}`);
        statsCache.remove(`penalty_records:${dojoId}:${seasonId}`);
      }
      statsCache.remove('player_stats:all:all');
      statsCache.remove('penalty_records:all:all');
      console.log(`[V620] 已清除赛季 ${seasonId} 相关缓存`);
    } catch (err) {
      console.error('[V100] 删除赛季失败:', err);
      throw err;
    }
  }
}

export const dojoSeasonService = new DojoSeasonService();
